import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { path, pipe, toPairs, filter } from 'ramda'
import { Link, withRouter } from 'react-router-dom'
import classNames from 'classnames'
import bindAllActions from '../../common/bindAllActions'
import SettingRadio from '../user/SettingRadio'
import SettingSelect from '../user/SettingSelect'
import PhoneProcess from '../user/PhoneProcess'
import AlertCrud from './Crud'
import Instances from './Instances'
import AlertSets from './AlertSets'
import HeatStressAlerts from './HeatStressAlerts'
import QuestionMark from '../../components/QuestionMark'
import AmbRadio from '../../components/AmbRadio'
import AwnSwitch from '../common/AwnSwitch'
import { isUserPhoneConfirmed, pluckClean, getDisplayKeys, getUserSetting, isCordova, isRole, isIos, isApp, hasHst } from '../../common/ambient'
import { getThemeObj } from '../../common/skinner'
import { CopyLink, Loader } from '../../components'
import { isPlus } from '../payment'
import { isDev } from '../../common/ambient/utils'
import { ALERT_FREQS, alertSetsAvail } from '.'
import AlertList from './AlertList'

const ALERT_TABS = {
  timeline: 'Timeline',
  alerts: 'My Alerts',
  group: 'Team Alerts',
  heatstress: 'Heat Stress Alerts'
}
class DefaultPage extends Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    alertActions: PropTypes.object.isRequired,
    userActions: PropTypes.object.isRequired,
    alert: PropTypes.object.isRequired,
    device: PropTypes.object.isRequired
  };

  state = {
    alertRecipientEmailInfo: {},
    tab: 'alerts',
    socialAppAlertExplain: false,
  }

  componentDidMount () {
    window.scrollTo(0, 0)
  }

  componentDidUpdate (prevProps) {
    // Scroll to top when switching tabs
    if (prevProps.location.pathname !== this.props.location.pathname) {
      window.scrollTo(0, 0)
    }
  }


  _socialPrefs () {
    const { user, userActions } = this.props
    const alertSettings = getUserSetting('alerts')(user) || {}

    // NORMALIZE: Convert old string format to array
    let socialTypes = []
    if (alertSettings.social) {
      if (typeof alertSettings.social === 'string') {
        // Migration mapping: 'all' -> ['email'], 'app' -> []
        socialTypes = alertSettings.social === 'all' ? ['email'] : []
      } else {
        socialTypes = alertSettings.social
      }
    } else {
      // Default: email enabled
      socialTypes = ['email']
    }

    const hasSocialType = type => socialTypes.includes(type)
    const hasAppDevice = path(['info', 'appDevices'], user)

    const socialChannel = [
      [<span>No&nbsp;notifications</span>, 'none'],
      ['Email', 'email'],
      ['App', 'app']
    ]

    const radios = socialChannel.map((arr) => {
      const label = arr[0]
      const key = arr[1]
      return (
        <AmbRadio
          key={key}
          label={label}
          selected={key === 'none' ? socialTypes.length === 0 : hasSocialType(key)}
          onClick={() => {
            // Validate: App requires app device
            if (key === 'app' && !hasAppDevice) {
              this.setState({ socialAppAlertExplain: true })
              return
            }

            // Toggle logic
            let newAlertSettings
            if (key === 'none') {
              newAlertSettings = Object.assign({}, alertSettings, { social: [] })
            } else {
              newAlertSettings = Object.assign({}, alertSettings, {
                social: hasSocialType(key)
                  ? socialTypes.filter(type => type !== key)
                  : socialTypes.concat(key)
              })
            }
            userActions.updateSetting('alerts', newAlertSettings)
          }}
        />
      )
    })

    // CONDITIONAL MESSAGES
    const messages = []
    if (hasSocialType('email')) {
      messages.push(<div key='email'>{this._emailMessage()}</div>)
    }

    // RENDER
    return (
      <div className='block social-comm'>
        <h3>Social Feed Notifications</h3>
        {this.state.socialAppAlertExplain && (
          <p className='warning'>
            App notifications are only available to users who have installed the Ambient Weather ios or android apps. <a href='https://ambientweather.com/sms-update' target='_blank' rel='noreferrer'>Learn more</a>.
          </p>
        )}
        <div className='prefs'>
          {radios}
        </div>
        <div className='message'>
          {messages}
        </div>
      </div>
    )
  }

  _emailMessage () {
    const { user, userActions } = this.props
    let message
    if (!path(['info', 'isVerified'], user)) {
      const link = user.sendConfirmationSucceeded
        ? <b>Confirmation Sent!</b>
        : <a
            className='btn btn-primary'
            onClick={() => {
              userActions.sendConfirmation({ user: user.info._id, confirmKey: true })
          }}
          >Resend</a>
      message = (
        <p className="warning">
          <span>Alerts will not be sent until your email address ({user.info.email}) is confirmed. </span>
          {link}
        </p>
      )} else {
      message = <p>We'll send emails to {user.info.email}.</p>
    }
    return message
  }

  _hasMultiSms () {
    return this.props.device.devices && this.props.device.devices.filter(hasHst).length > 0
  }

  render () {
    const { socialActions, userActions, user, match } = this.props
    const { alertRecipientEmailInfo, appAlertExplain } = this.state
    const { tab = 'alerts' } = match.params || {}
    const { fetchUserPending } = user
    const alertSettings = getUserSetting('alerts')(user) || {}
    const alertChannel = [
      [<span>No&nbsp;notifications</span>, 'none'],
      ['Email', 'email'],
      ['App', 'app'],
      ['SMS', 'sms']
    ]
    let radios = alertChannel.map((arr) => {
      const label = arr[0]
      const key = arr[1]
      return (
        <SettingRadio
          key={key}
          setting='alerts'
          settingKey='type'
          value={key}
          label={label}
        />
      )
    })
    let alertTypes = []
    if (alertSettings.type) {
      alertTypes = typeof alertSettings.type === 'string' ? [alertSettings.type] : alertSettings.type
    }
    const hasAlertType = type => alertTypes.includes(type)

    const hasAppDevice = path(['info', 'appDevices'], user)

    radios = alertChannel.map((arr) => {
      const label = arr[0]
      const key = arr[1]
      return (
        <AmbRadio
          key={key}
          label={label}
          className={key === 'sms' && 'plus'}
          selected={key === 'none' ? alertTypes.length === 0 : hasAlertType(key)}
          onClick={() => {
            if (key === 'sms' && !isPlus(user)) {
              userActions.doModal({
                type: 'plus-subscription',
                data: {
                  message: 'SMS alerts are only available to AWN+ subscribers.'
                }
              })
              // dont let them add it
              if (!hasAlertType(key)) {
                return
              }
            }
            if (key === 'app' && !hasAppDevice) {
              this.setState({ appAlertExplain: true })
              return
            }
            let newAlertSettings
            if (key === 'none') {
              newAlertSettings = Object.assign({}, alertSettings, { type: [] })
            } else {
              newAlertSettings = Object.assign({}, alertSettings, { type: hasAlertType(key) ? alertTypes.filter((type) => type !== key) : alertTypes.concat(key) })
            }
            userActions.updateSetting('alerts', newAlertSettings)
          }}
        />
      )
    })
    const messages = []
    if (hasAlertType('email')) {
      messages.push(<div key='email'>{this._emailMessage()}</div>)
    }
    if (hasAlertType('sms')) {
      const yesPhone = (
        <>
          <p>
            We'll send texts to {user.info.phone}
            <span className='pull-right'>
              SMS messages remaining: {path(['info', 'smsBankUpdated'], user) ? (user.info.smsBank || 0) : 10}
              <QuestionMark link="https://ambientweather.com/faqs/question/view/id/12621/" />
            </span>
            <small>
              <a className='glyphicon glyphicon-trash' onClick={() => {
                if (window.confirm('Are you sure?')) {
                  userActions.patch(user.info._id, { phone: '', phoneConfirmKey: false })
                }
              }}
              />
            </small>
          </p>
        </>
      )
      messages.push(<div key='sms'>{isUserPhoneConfirmed(user) ? yesPhone : <PhoneProcess />}</div>)
    }
    if (hasAlertType('app')) {
      const devicesWithPermission = pipe(
        path(['info', 'appDevices']),
        toPairs,
        filter(([key, obj]) => obj.notificationsEnabled)
      )(user)
      if (!fetchUserPending && isApp() && devicesWithPermission.length < 1) {
        messages.push(
          <p key='app'>
            You have no devices with permission to receive push notifications.
            {isApp() && <span> Please go to Settings &gt; Notifcations &gt; Ambient Weather to enable push notifications</span>}
          </p>
        )
      }
    }
    let freq = ''
    if (alertTypes.length > 0) {
      freq = (
        <SettingSelect
          setting='alerts'
          settingKey='freq'
          values={ALERT_FREQS}
          label='Notification Time Window'
        />
      )
    }
    let show = (
      <>
        {this._socialPrefs()}
        <div className='block'>
          <h3>Device Alerts<QuestionMark link="https://ambientweather.com/faqs/question/view/id/1793/" /></h3>
          {appAlertExplain && (
            <p className='warning'>
              App notifications are only available to users who have installed the Ambient Weather ios or android apps. <a href='https://ambientweather.com/sms-update' target='_blank' rel='noreferrer'>Learn more</a>.
            </p>
          )}
          <div className='prefs'>
            {radios}
            {freq}
          </div>
          <div className='message'>
            {messages}
          </div>
        </div>
        <div className='block'>
          <h3>Customizations</h3>
          <AlertCrud />
        </div>
      </>
    )
    if (tab === 'group') {
      show = <AlertSets />
    }
    if (tab === 'timeline') {
      show = (
        <div className='block clearfix'>
          <h3>Your alerts</h3>
          <p>Find all your alerts here{alertSetsAvail(user) ? ', including personal and team notifications.' : '.'}</p>
          <div className='legend'>
            <div>
              <i className='glyphicon glyphicon-user' />
              <span>Personal Alerts</span>
            </div>
            <div>
              <i className='glyphicon glyphicon-team-alerts' />
              <span>Team Alerts</span>
            </div>
          </div>
           <Instances />
        </div>
      )
    }
    if (tab === 'heatstress') {
      show = <HeatStressAlerts />
    }
    return (
      <div className='alert-default-page'>
        <header className='main'>
          <h1>
            Alerts
          </h1>
        </header>
        <div className='tabs'>
          {Object.keys(ALERT_TABS)
            .filter(key => key !== 'heatstress' || this._hasMultiSms())
            .map(key => <Link key={key} to={`/alerts${key === 'alerts' ? '' : `/${key}`}`} className={classNames({
              active: tab === key
            }, 'tab', key)}>{ALERT_TABS[key]}</Link>)}
        </div>
        <div className='page-body'>
          {show}
        </div>
      </div>
    )
  }
}
export default bindAllActions(withRouter(DefaultPage))

DefaultPage.displayName = 'DefaultPage'
